import {
  Box,
  MenuItem,
  Button,
  FormControl,
  IconButton,
  LinearProgress,
  linearProgressClasses,
  Link,
  styled,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import { Filter } from '../../../components/_WMS/FilterComponent'
import React, { Fragment, forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import HelpIcon from '@mui/icons-material/Help'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { Selector } from '../../../components/Selector'
import GenericTransferElements from '../../../components/_WMS/GenericTransferElementsComponent/GenericTransferElementsComponent'
import {
  getInventoryCheckTemplate,
  getInventoryCheckUsersList,
  postInventoryCheck
} from '../api'
import { useApi } from '../../../hooks/useApi'
import {
  InventoryCheckUser,
  InventoryCheckUserDetailnfoDto
} from '../entities/InventoryCheck'
import { Loading } from '../../../components/UI/Loading/Loading'
import { Status } from '../../../hooks/useFetchState'
import { useWarehouses } from '../../../hooks'
import { eTypeInventoryCheck } from '../../../enums/eInventoryCheck'

const BorderLinearProgress = styled(LinearProgress)(() => ({
  height: 8,
  borderRadius: 4,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: '#EEEEEE'
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 4,
    backgroundColor: '#1a90ff'
  }
}))

interface CreateInventoryCheckModalProps {
  handleSnackbar: (message: string, success: boolean) => void
  handleCloseModal: () => void
  requestInventoryCheck: (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => void
  onCanSubmitChange?: (canSubmit: boolean) => void
}

export interface CreateInventoryCheckModalRef {
  submit: () => void
}

export const CreateInventoryCheckModal = forwardRef<
  CreateInventoryCheckModalRef,
  CreateInventoryCheckModalProps
>(({
  handleSnackbar,
  handleCloseModal,
  requestInventoryCheck,
  onCanSubmitChange
}, ref) => {
  const [progress, setProgress] = useState<number>(0)
  const [rigthUser, setRigthUser] = useState<InventoryCheckUser[]>([])

  const [selectedFiles, setSelectedFiles] = useState<any[] | undefined>(
    undefined
  )

  const [qtyCycles, setQtyCycles] = useState<number>(2)
  const [promiseDate, setpromiseDate] = useState<string>('')
  const selectedTypeOrder = eTypeInventoryCheck.Ciclicos

  const [loading, setLoading] = useState<boolean>(false)

  const { state, callApi } = useApi<InventoryCheckUserDetailnfoDto>()

  const { state: requestLabelState, callApi: requestDownloadTemplate } =
    useApi()
  const [selectedWarehouse, setSelectedWarehouse] = useState<number>(-1)

  const {
    warehousesState: { warehouses },
    getWarehouses
  } = useWarehouses()

  useEffect(() => {
    if (warehouses?.length === 0) {
      getWarehouses()
    } else if (warehouses?.length === 1) {
      setSelectedWarehouse(warehouses[0].id)
    }
  }, [warehouses])

  const handleSetWarehouse = (id: number) => {
    setSelectedWarehouse(id)
  }

  const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setpromiseDate(e.target.value)
  }

  const handleQtyCyclesChange = (qty: number) => {
    setQtyCycles(qty)
  }

  const handleSelectFile = (event: any) => {
    setSelectedFiles(event.target.files)
    setProgress(0)
  }

  const canSubmit =
    !!selectedFiles && rigthUser.length > 0 && selectedWarehouse > 0 && !loading

  useEffect(() => {
    onCanSubmitChange?.(canSubmit)
  }, [canSubmit])

  const handleSubmit = () => {
    setLoading(true)

    const formData = new FormData()

    formData.append('typeOrderId', selectedTypeOrder.toString())
    formData.append('qtyCycles', qtyCycles.toString())
    formData.append('promiseDate', promiseDate)
    formData.append('warehouseId', `${selectedWarehouse}`)

    rigthUser.forEach((user) => {
      formData.append('users', user.userId.toString())
    })

    if (selectedFiles && selectedFiles.length > 0)
      formData.append('file', selectedFiles[0])

    callApi(postInventoryCheck(formData))
      .then(() => {
        handleSnackbar('Carga realizada exitosamente', true)
        requestInventoryCheck()
      })
      .catch((error) => {
        requestInventoryCheckUsers()
        handleSnackbar(error, false)
      })
      .finally(() => {
        setLoading(false)
        handleCloseModal()
      })
  }

  useImperativeHandle(ref, () => ({
    submit: () => {
      if (canSubmit) handleSubmit()
    }
  }))

  const handleDownloadTemplate = () => {
    setLoading(true)

    requestDownloadTemplate(getInventoryCheckTemplate())
      .then((response) => {
        const raw = window.atob(response.file)
        const uInt8Array = new Uint8Array(raw.length)
        for (let i = 0; i < raw.length; ++i) {
          uInt8Array[i] = raw.charCodeAt(i)
        }
        const link = document.createElement('a')
        const blob = new Blob([uInt8Array], {
          type: response.mimeType
        })
        link.style.display = 'none'
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', response.name)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
      .catch((error) => {
        handleSnackbar(error, false)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleRightItemsChange = (updatedRightItems: InventoryCheckUser[]) => {
    setRigthUser(updatedRightItems)
  }

  const requestInventoryCheckUsers = () => {
    setLoading(true)
    callApi(getInventoryCheckUsersList())
      .then(() => {
        setLoading(false)
      })
      .catch((error) => {
        handleSnackbar(error, false)
      })
    setLoading(false)
  }

  const qtyCyclesComponent = [1, 2].map((cycle) => (
    <MenuItem key={cycle} value={cycle}>
      {cycle}
    </MenuItem>
  ))

  useEffect(() => {
    requestInventoryCheckUsers()
  }, [])

  if (state.status === Status.Loading || loading) {
    return (
      <Box display='flex' justifyContent='center' py={4}>
        <Loading />
      </Box>
    )
  }

  return (
    <Fragment>
      {/* Sección: Configuración */}
      <Box style={{ borderLeft: '4px solid #1976d2', borderRadius: 8, backgroundColor: '#f5f5f5', padding: '0 16px 12px', marginBottom: 4 }}>
        <Typography variant='overline' style={{ fontWeight: 700, letterSpacing: 1.2, color: '#1976d2', display: 'block', marginBottom: 4 }}>Configuración</Typography>
        <Grid container spacing={2} alignItems='center'>
          <Grid size='grow'>
            <FormControl fullWidth>
              <Selector
                labelId='selectedWarehouse'
                label='Almacén'
                item={
                  warehouses &&
                  warehouses.map((item) => ({
                    value: item.id,
                    name: item.warehouseName
                  }))
                }
                defaultLabel='Almacén'
                onChange={(e: any) =>
                  handleSetWarehouse(e.target.value as number)
                }
                id='warehouseId'
                name='warehouseId'
                value={selectedWarehouse}
                variant='outlined'
              />
            </FormControl>
          </Grid>

          <Grid size={3}>
            <FormControl fullWidth>
              <TextField
                id='qtyCycles'
                name='qtyCycles'
                label='Cantidad de Ciclos'
                value={qtyCycles}
                onChange={(e: any) =>
                  handleQtyCyclesChange(e.target.value as number)
                }
                variant='outlined'
                select
                fullWidth
              >
                {qtyCyclesComponent}
              </TextField>
            </FormControl>
          </Grid>

          <Grid size='grow'>
            <FormControl fullWidth>
              <TextField
                id='promiseDate'
                name='promiseDate'
                label='Fecha Compromiso'
                type='date'
                value={promiseDate}
                onChange={handleDatePickerChange}
                variant='outlined'
                InputLabelProps={{ shrink: true }}
              />
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Sección: Usuarios */}
      <Box style={{ borderLeft: '4px solid #1976d2', borderRadius: 8, backgroundColor: '#f5f5f5', padding: '0 16px 12px', marginTop: 8 }}>
        <Typography variant='overline' style={{ fontWeight: 700, letterSpacing: 1.2, color: '#1976d2', display: 'block', marginBottom: 4 }}>Asignación de usuarios</Typography>
      <GenericTransferElements
        availableItems={state.data?.availableUsers || []}
        items={rigthUser || []}
        getKey={(user) => user.userId}
        getLabel={(user) => user.username}
        leftTitle='Usuarios Disponibles'
        rightTitle='Usuarios Asignados'
        onRightItemsChange={handleRightItemsChange}
      />
      </Box>

      {/* Sección: Archivo */}
      <Box
        style={{
          borderLeft: '4px solid #1976d2',
          borderRadius: 8,
          padding: '0 16px 12px',
          backgroundColor: '#f9f9f9',
          marginTop: 8,
        }}
      >
        <Typography variant='overline' style={{ fontWeight: 700, letterSpacing: 1.2, color: '#1976d2', display: 'block', marginBottom: 4 }}>Carga de archivo</Typography>
        <Grid container spacing={2} alignItems='center'>
          <Grid size='auto'>
            <input
              id='btn-upload'
              name='btn-upload'
              style={{ display: 'none' }}
              type='file'
              onChange={handleSelectFile}
              accept='.xls,.xlsx'
            />
            <label htmlFor='btn-upload'>
              <Button
                variant='outlined'
                color='primary'
                component='span'
                startIcon={<UploadFileIcon />}
                style={{ whiteSpace: 'nowrap' }}
              >
                Seleccionar Archivo
              </Button>
            </label>
          </Grid>

          <Grid size='grow'>
            <Typography
              variant='body2'
              color={selectedFiles ? 'textPrimary' : 'textSecondary'}
              noWrap
            >
              {selectedFiles && selectedFiles.length > 0
                ? selectedFiles[0].name
                : 'Ningún archivo seleccionado'}
            </Typography>
          </Grid>
        </Grid>

        <Box mt={1} display='flex' alignItems='center'>
          <Typography variant='caption' color='textSecondary'>
            Puede descargar una <b>plantilla</b> desde el siguiente link:{' '}
            {requestLabelState.status === Status.Loading ? (
              <Loading />
            ) : (
              <Link style={{ cursor: 'pointer' }} onClick={handleDownloadTemplate}>
                Descargar Plantilla
              </Link>
            )}
          </Typography>
          <Tooltip
            title='Seleccione un archivo Excel (.xls, .xlsx) y presione Cargar para procesarlo'
            placement='top'
          >
            <IconButton size='small' style={{ marginLeft: 4 }}>
              <HelpIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>

        <Box mt={1}>
          <BorderLinearProgress variant='determinate' value={progress} />
          <Typography
            variant='caption'
            color='textSecondary'
            style={{ textAlign: 'end', display: 'block' }}
          >
            {`${progress}%`}
          </Typography>
        </Box>
      </Box>
    </Fragment>
  )
})
